/**
 * Archer Renderer - Specialized renderer for archer units with bow attack animation
 * Features:
 * - Procedural bow-drawing attack animation via custom shaders
 * - Arrow nocking and release motion
 * - Smooth bow arm pull animation
 * - Walk animation with bow-ready stance
 */

import * as THREE from 'three';
import type { FactionId, Entity } from '@/types';
import { EventBus } from '@/core/events/EventBus';
import { COLORS, FACTION_COLORS } from './unitConstants';

const MAX_ARCHERS = 500;

// Animation group IDs encoded in vertex attribute
export const ARCHER_ANIM_GROUP = {
  STATIC: 0,       // No animation
  BODY: 1,         // Body - lean during attack
  HEAD: 2,         // Head - follows body, aim during attack
  LEFT_ARM: 3,     // Left arm (holding bow - extends forward)
  RIGHT_ARM: 4,    // Right arm (drawing string - pulls back)
  LEFT_LEG: 5,     // Left leg
  RIGHT_LEG: 6,    // Right leg
  BOW: 7,          // Bow - held steady by left hand
  ARROW: 8,        // Arrow - moves with draw
  BOWSTRING: 9,    // Bowstring - stretches during draw
  QUIVER: 10,      // Quiver on back
};

interface ArcherData {
  entity: Entity;
  x: number;
  y: number;
  z: number;
  factionId: FactionId;
  rotation: number;
  speed: number;           // Movement speed for walk animation
  animPhase: number;       // Animation phase offset
  attackTime: number;      // Time when attack started (-1 = not attacking)
  attackDuration: number;  // Duration of attack animation
}

// Vertex shader with bow attack animation
const archerVertexShader = `
  attribute vec3 color;
  attribute float animGroup;

  uniform float uTime;

  // Per-instance attributes
  attribute float instanceSpeed;
  attribute float instanceAnimPhase;
  attribute float instanceAttackTime;

  varying vec3 vColor;
  varying vec3 vNormal;
  varying vec3 vWorldPosition;
  varying float vArrowVisible;

  // Animation parameters
  const float PI = 3.14159265359;
  const float WALK_FREQUENCY = 4.0;
  const float ATTACK_DURATION = 0.65;  // Attack animation duration in seconds

  vec3 animateVertex(vec3 pos, float group, float time, float speed, float phase, float attackTime, out float arrowVisible) {
    vec3 animated = pos;
    arrowVisible = 1.0;

    // Calculate attack progress (0 = not attacking, 0-1 = attack progress)
    float attackProgress = 0.0;
    float attackPhase = 0.0;
    if (attackTime >= 0.0) {
      float timeSinceAttack = time - attackTime;
      if (timeSinceAttack < ATTACK_DURATION) {
        attackProgress = timeSinceAttack / ATTACK_DURATION;
        // Attack phases: 0-0.4 = draw bow, 0.4-0.5 = hold/aim, 0.5-0.55 = release, 0.55-1.0 = recover
        if (attackProgress < 0.4) {
          attackPhase = attackProgress / 0.4;  // 0-1 draw
        } else if (attackProgress < 0.5) {
          attackPhase = 1.0 + (attackProgress - 0.4) / 0.1;  // 1-2 aim
        } else if (attackProgress < 0.55) {
          attackPhase = 2.0 + (attackProgress - 0.5) / 0.05;  // 2-3 release
        } else {
          attackPhase = 3.0 + (attackProgress - 0.55) / 0.45;  // 3-4 recover
        }
      }
    }

    // Arrow disappears after release
    if (attackPhase >= 2.5) {
      arrowVisible = 0.0;
    }

    // Walking animation
    float walkIntensity = clamp(speed, 0.0, 1.0);
    float t = time * WALK_FREQUENCY + phase;

    // Idle sway - subtle for archer
    float idleSway = sin(time * 1.3 + phase) * 0.002 * (1.0 - walkIntensity);
    float idleBreath = sin(time * 0.9 + phase) * 0.001 * (1.0 - walkIntensity);

    if (group < 0.5) {
      // STATIC - no animation
      return animated;
    }

    // Apply idle sway to all animated parts
    animated.z += idleSway;

    if (group < 1.5) {
      // BODY - slight lean during attack
      float walkBob = sin(t * 2.0) * 0.002 * walkIntensity;
      animated.y += walkBob + idleBreath;

      if (attackPhase > 0.0) {
        float leanAmount = 0.0;
        if (attackPhase < 1.0) {
          // Draw - body braces slightly back
          leanAmount = -0.008 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Aim - hold position
          leanAmount = -0.008;
        } else if (attackPhase < 3.0) {
          // Release - body follows through slightly
          float releasePhase = attackPhase - 2.0;
          leanAmount = -0.008 + 0.015 * releasePhase;
        } else {
          // Recover
          float recoverPhase = attackPhase - 3.0;
          leanAmount = 0.007 * (1.0 - recoverPhase);
        }
        animated.z += leanAmount;
      }
    }
    else if (group < 2.5) {
      // HEAD - follows body, focus on target during aim
      float walkBob = sin(t * 2.0 + 0.2) * 0.0015 * walkIntensity;
      animated.y += walkBob + idleBreath;

      if (attackPhase > 0.0) {
        float focusForward = 0.0;
        if (attackPhase < 1.0) {
          focusForward = 0.005 * attackPhase;
        } else if (attackPhase < 2.0) {
          focusForward = 0.005;
        } else if (attackPhase < 3.0) {
          float releasePhase = attackPhase - 2.0;
          focusForward = 0.005 - 0.003 * releasePhase;
        } else {
          float recoverPhase = attackPhase - 3.0;
          focusForward = 0.002 * (1.0 - recoverPhase);
        }
        animated.z += focusForward;
      }
    }
    else if (group < 3.5) {
      // LEFT_ARM (bow arm) - extends forward during attack
      float walkSwing = sin(t) * 0.005 * walkIntensity;
      animated.z += walkSwing * 0.3;

      if (attackPhase > 0.0) {
        float extendAmount = 0.0;
        float raiseAmount = 0.0;
        if (attackPhase < 1.0) {
          // Draw - arm extends forward
          extendAmount = 0.015 * attackPhase;
          raiseAmount = 0.01 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Aim - hold extended
          extendAmount = 0.015;
          raiseAmount = 0.01;
        } else if (attackPhase < 3.0) {
          // Release - slight recoil
          float releasePhase = attackPhase - 2.0;
          extendAmount = 0.015 - 0.005 * releasePhase;
          raiseAmount = 0.01 - 0.003 * releasePhase;
        } else {
          // Recover
          float recoverPhase = attackPhase - 3.0;
          extendAmount = 0.01 * (1.0 - recoverPhase);
          raiseAmount = 0.007 * (1.0 - recoverPhase);
        }
        animated.z += extendAmount;
        animated.y += raiseAmount;
      }
    }
    else if (group < 4.5) {
      // RIGHT_ARM (draw arm) - pulls back dramatically during attack
      float walkSwing = -sin(t) * 0.005 * walkIntensity;
      animated.z += walkSwing * 0.3;

      if (attackPhase > 0.0) {
        float pullBack = 0.0;
        float raiseAmount = 0.0;
        if (attackPhase < 1.0) {
          // Draw - arm pulls back to chest
          pullBack = -0.04 * attackPhase;
          raiseAmount = 0.015 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Aim - hold at full draw
          pullBack = -0.04;
          raiseAmount = 0.015;
        } else if (attackPhase < 3.0) {
          // Release - arm snaps forward
          float releasePhase = attackPhase - 2.0;
          pullBack = -0.04 + 0.055 * releasePhase;
          raiseAmount = 0.015 - 0.01 * releasePhase;
        } else {
          // Recover
          float recoverPhase = attackPhase - 3.0;
          pullBack = 0.015 * (1.0 - recoverPhase);
          raiseAmount = 0.005 * (1.0 - recoverPhase);
        }
        animated.z += pullBack;
        animated.y += raiseAmount;
      }
    }
    else if (group < 5.5) {
      // LEFT_LEG
      float cycle = t;
      float lift = max(sin(cycle), 0.0) * 0.007 * walkIntensity;
      float swing = sin(cycle) * 0.005 * walkIntensity;
      animated.y += lift;
      animated.z += swing;

      // Brace during aim
      if (attackPhase >= 1.0 && attackPhase < 3.0) {
        animated.z -= 0.003;
      }
    }
    else if (group < 6.5) {
      // RIGHT_LEG
      float cycle = t + PI;
      float lift = max(sin(cycle), 0.0) * 0.007 * walkIntensity;
      float swing = sin(cycle) * 0.005 * walkIntensity;
      animated.y += lift;
      animated.z += swing;

      // Step back slightly during draw
      if (attackPhase > 0.0 && attackPhase < 1.0) {
        animated.z -= 0.004 * attackPhase;
      } else if (attackPhase >= 1.0 && attackPhase < 3.0) {
        animated.z -= 0.004;
      } else if (attackPhase >= 3.0) {
        float recoverPhase = attackPhase - 3.0;
        animated.z -= 0.004 * (1.0 - recoverPhase);
      }
    }
    else if (group < 7.5) {
      // BOW - follows left arm, tilts during draw
      float idleBowSway = sin(time * 0.7 + phase) * 0.002 * (1.0 - walkIntensity);
      animated.x += idleBowSway;

      if (attackPhase > 0.0) {
        float extendAmount = 0.0;
        float raiseAmount = 0.0;
        float tiltAmount = 0.0;
        if (attackPhase < 1.0) {
          extendAmount = 0.015 * attackPhase;
          raiseAmount = 0.01 * attackPhase;
          tiltAmount = -0.008 * attackPhase;  // Tilt bow slightly
        } else if (attackPhase < 2.0) {
          extendAmount = 0.015;
          raiseAmount = 0.01;
          tiltAmount = -0.008;
        } else if (attackPhase < 3.0) {
          float releasePhase = attackPhase - 2.0;
          extendAmount = 0.015 - 0.005 * releasePhase;
          raiseAmount = 0.01 - 0.003 * releasePhase;
          tiltAmount = -0.008 + 0.003 * releasePhase;
        } else {
          float recoverPhase = attackPhase - 3.0;
          extendAmount = 0.01 * (1.0 - recoverPhase);
          raiseAmount = 0.007 * (1.0 - recoverPhase);
          tiltAmount = -0.005 * (1.0 - recoverPhase);
        }
        animated.z += extendAmount;
        animated.y += raiseAmount;
        animated.x += tiltAmount;
      }
    }
    else if (group < 8.5) {
      // ARROW - moves back with draw, disappears on release
      if (attackPhase > 0.0) {
        float drawBack = 0.0;
        if (attackPhase < 1.0) {
          drawBack = -0.06 * attackPhase;
        } else if (attackPhase < 2.0) {
          drawBack = -0.06;
        }
        // After release, arrow flies away (handled by effect system)
        animated.z += drawBack;

        // Same raise as bow arm
        float raiseAmount = 0.0;
        if (attackPhase < 1.0) {
          raiseAmount = 0.01 * attackPhase;
        } else if (attackPhase < 2.0) {
          raiseAmount = 0.01;
        }
        animated.y += raiseAmount;
      }
    }
    else if (group < 9.5) {
      // BOWSTRING - stretches back during draw
      if (attackPhase > 0.0) {
        float stretchBack = 0.0;
        if (attackPhase < 1.0) {
          stretchBack = -0.05 * attackPhase;
        } else if (attackPhase < 2.0) {
          stretchBack = -0.05;
        } else if (attackPhase < 3.0) {
          // String snaps forward on release
          float releasePhase = attackPhase - 2.0;
          stretchBack = -0.05 * (1.0 - releasePhase * 3.0);
          stretchBack = max(stretchBack, 0.0);  // Don't go past rest position
        }
        animated.z += stretchBack;
      }
    }
    else if (group < 10.5) {
      // QUIVER - subtle sway with body
      float walkBob = sin(t * 2.0) * 0.002 * walkIntensity;
      animated.y += walkBob;

      float quiverSway = sin(t * 1.5 + phase) * 0.003 * walkIntensity;
      animated.x += quiverSway;
    }

    return animated;
  }

  void main() {
    vColor = color;
    vNormal = normalize(normalMatrix * normal);

    // Get instance-specific animation parameters
    float speed = instanceSpeed;
    float phase = instanceAnimPhase;
    float attackTime = instanceAttackTime;

    // Animate vertex
    float arrowVisible;
    vec3 animatedPosition = animateVertex(position, animGroup, uTime, speed, phase, attackTime, arrowVisible);
    vArrowVisible = arrowVisible;

    vec4 worldPosition = instanceMatrix * vec4(animatedPosition, 1.0);
    vWorldPosition = worldPosition.xyz;

    vec4 mvPosition = modelViewMatrix * worldPosition;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

// Fragment shader with faction colors
const archerFragmentShader = `
  uniform vec3 primaryColor;
  uniform vec3 secondaryColor;
  uniform vec3 ambientLightColor;
  uniform vec3 directionalLightColor;
  uniform vec3 directionalLightDirection;

  varying vec3 vColor;
  varying vec3 vNormal;
  varying vec3 vWorldPosition;
  varying float vArrowVisible;

  void main() {
    // Discard arrow fragments when arrow is released
    // Detect arrow by checking for wood color (arrow shaft)
    bool isArrow = (vColor.r > 0.5 && vColor.r < 0.6 && vColor.g > 0.25 && vColor.g < 0.35 && vColor.b < 0.15);
    if (isArrow && vArrowVisible < 0.5) {
      discard;
    }

    vec3 finalColor = vColor;

    // Faction color replacement
    if (vColor.r > 0.9 && vColor.g < 0.2 && vColor.b < 0.2) {
      finalColor = primaryColor;
    } else if (vColor.g > 0.9 && vColor.r < 0.2 && vColor.b < 0.2) {
      finalColor = secondaryColor;
    }

    // Lighting
    vec3 normal = normalize(vNormal);
    vec3 lightDir = normalize(directionalLightDirection);

    float NdotL = dot(normal, lightDir);
    float diff = max(NdotL, 0.0);
    float wrap = max(NdotL * 0.5 + 0.5, 0.0);

    // Rim lighting
    vec3 viewDir = normalize(cameraPosition - vWorldPosition);
    float rim = 1.0 - max(dot(viewDir, normal), 0.0);
    rim = pow(rim, 4.0) * 0.15;

    // Combine lighting
    vec3 ambient = ambientLightColor * 0.55;
    vec3 diffuse = directionalLightColor * diff * 0.35;
    vec3 fill = directionalLightColor * wrap * 0.15;
    vec3 rimColor = vec3(1.0, 0.95, 0.9) * rim;

    vec3 litColor = finalColor * (ambient + diffuse + fill) + rimColor;

    // Slight color variation
    litColor *= 0.95 + 0.1 * fract(sin(dot(vWorldPosition.xz, vec2(12.9898, 78.233))) * 43758.5453);

    gl_FragColor = vec4(litColor, 1.0);
  }
`;

export class ArcherRenderer {
  private scene: THREE.Scene;
  private instancedMeshes: Map<FactionId, THREE.InstancedMesh> = new Map();
  private archerData: ArcherData[] = [];
  private entityMap: Map<Entity, ArcherData> = new Map(); // O(1) lookup
  private materials: Map<FactionId, THREE.ShaderMaterial> = new Map();
  private dummy = new THREE.Object3D();
  private time = 0;

  // Instance attributes for animation
  private speedAttribute: THREE.InstancedBufferAttribute | null = null;
  private phaseAttribute: THREE.InstancedBufferAttribute | null = null;
  private attackTimeAttribute: THREE.InstancedBufferAttribute | null = null;

  // Event listener cleanup
  private attackEventHandler: ((data: { attackerId: Entity }) => void) | null = null;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.createGeometryAndMeshes();
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Listen for attack events to trigger attack animation
    this.attackEventHandler = (data: { attackerId: Entity }) => {
      const archer = this.entityMap.get(data.attackerId);
      if (archer) {
        archer.attackTime = this.time;
        archer.attackDuration = 0.65;
      }
    };
    EventBus.on('unit:attack', this.attackEventHandler);
  }

  private createGeometryAndMeshes(): void {
    const geometry = this.createArcherGeometry();

    // Create instance attributes for animation
    const speeds = new Float32Array(MAX_ARCHERS);
    const phases = new Float32Array(MAX_ARCHERS);
    const attackTimes = new Float32Array(MAX_ARCHERS);
    for (let i = 0; i < MAX_ARCHERS; i++) {
      speeds[i] = 0;
      phases[i] = Math.random() * Math.PI * 2;
      attackTimes[i] = -1;  // Not attacking
    }

    this.speedAttribute = new THREE.InstancedBufferAttribute(speeds, 1);
    this.phaseAttribute = new THREE.InstancedBufferAttribute(phases, 1);
    this.attackTimeAttribute = new THREE.InstancedBufferAttribute(attackTimes, 1);

    geometry.setAttribute('instanceSpeed', this.speedAttribute);
    geometry.setAttribute('instanceAnimPhase', this.phaseAttribute);
    geometry.setAttribute('instanceAttackTime', this.attackTimeAttribute);

    // Create mesh for each faction
    for (const [factionId, colors] of Object.entries(FACTION_COLORS)) {
      const material = new THREE.ShaderMaterial({
        vertexShader: archerVertexShader,
        fragmentShader: archerFragmentShader,
        uniforms: {
          primaryColor: { value: colors.primary },
          secondaryColor: { value: colors.secondary },
          ambientLightColor: { value: new THREE.Color(0xffffff) },
          directionalLightColor: { value: new THREE.Color(0xffffff) },
          directionalLightDirection: { value: new THREE.Vector3(0.5, 1.0, 0.3).normalize() },
          uTime: { value: 0 },
        },
      });
      this.materials.set(factionId as FactionId, material);

      const mesh = new THREE.InstancedMesh(geometry, material, MAX_ARCHERS);
      mesh.count = 0;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.frustumCulled = false;
      this.instancedMeshes.set(factionId as FactionId, mesh);
      this.scene.add(mesh);
    }
  }

  private createArcherGeometry(): THREE.BufferGeometry {
    const parts: THREE.BufferGeometry[] = [];
    const colors: THREE.Color[] = [];
    const animGroups: number[] = [];

    // ==================== 头部 ====================
    const head = new THREE.SphereGeometry(0.08, 10, 8);
    head.translate(0, 0.52, 0);
    parts.push(head);
    colors.push(COLORS.skin);
    animGroups.push(ARCHER_ANIM_GROUP.HEAD);

    // ==================== 头盔（轻型） ====================
    // 头盔主体（半球形，比剑士小）
    const helmetDome = new THREE.SphereGeometry(
      0.082,
      8,
      6,
      0,
      Math.PI * 2,
      0,
      Math.PI * 0.5
    );
    helmetDome.translate(0, 0.54, 0);
    parts.push(helmetDome);
    colors.push(COLORS.archerRed);
    animGroups.push(ARCHER_ANIM_GROUP.HEAD);

    // 头盔金边
    const helmetRim = new THREE.TorusGeometry(0.078, 0.008, 4, 12);
    helmetRim.rotateX(Math.PI / 2);
    helmetRim.translate(0, 0.54, 0);
    parts.push(helmetRim);
    colors.push(COLORS.gold);
    animGroups.push(ARCHER_ANIM_GROUP.HEAD);

    // ==================== 躯干（轻甲） ====================
    // 上身
    const torso = new THREE.CylinderGeometry(0.065, 0.075, 0.14, 8);
    torso.translate(0, 0.36, 0);
    parts.push(torso);
    colors.push(COLORS.archerRed);
    animGroups.push(ARCHER_ANIM_GROUP.BODY);

    // 皮质护胸
    const chestGuard = new THREE.BoxGeometry(0.08, 0.06, 0.015);
    chestGuard.translate(0, 0.38, 0.065);
    parts.push(chestGuard);
    colors.push(COLORS.woodDark);
    animGroups.push(ARCHER_ANIM_GROUP.BODY);

    // 腰带
    const belt = new THREE.CylinderGeometry(0.078, 0.078, 0.02, 8);
    belt.translate(0, 0.27, 0);
    parts.push(belt);
    colors.push(COLORS.gold);
    animGroups.push(ARCHER_ANIM_GROUP.BODY);

    // 短裙甲（便于移动）
    const skirt = new THREE.CylinderGeometry(0.075, 0.09, 0.07, 8);
    skirt.translate(0, 0.21, 0);
    parts.push(skirt);
    colors.push(COLORS.archerRed);
    animGroups.push(ARCHER_ANIM_GROUP.BODY);

    // ==================== 箭袋（背在背上，斜向） ====================
    const quiver = new THREE.CylinderGeometry(0.03, 0.035, 0.18, 6);
    quiver.rotateX(0.25);
    quiver.rotateZ(0.35);
    quiver.translate(0.05, 0.36, -0.06);
    parts.push(quiver);
    colors.push(COLORS.woodDark);
    animGroups.push(ARCHER_ANIM_GROUP.QUIVER);

    // 箭袋带
    const strap = new THREE.BoxGeometry(0.012, 0.16, 0.006);
    strap.rotateZ(-0.2);
    strap.translate(-0.015, 0.36, 0.04);
    parts.push(strap);
    colors.push(COLORS.wood);
    animGroups.push(ARCHER_ANIM_GROUP.QUIVER);

    // 箭袋中的箭头
    for (let i = 0; i < 3; i++) {
      const arrowTip = new THREE.ConeGeometry(0.005, 0.018, 4);
      const angle = (i / 3) * Math.PI * 2;
      arrowTip.translate(
        0.05 + Math.cos(angle) * 0.012,
        0.47,
        -0.06 + Math.sin(angle) * 0.012
      );
      parts.push(arrowTip);
      colors.push(COLORS.steel);
      animGroups.push(ARCHER_ANIM_GROUP.QUIVER);
    }

    // ==================== 左臂（向前伸出，握弓） ====================
    // 左上臂 - 从肩膀向前伸
    const leftUpperArm = new THREE.CylinderGeometry(0.022, 0.02, 0.07, 6);
    leftUpperArm.rotateX(-Math.PI / 2.5);
    leftUpperArm.rotateZ(0.2);
    leftUpperArm.translate(-0.07, 0.40, 0.035);
    parts.push(leftUpperArm);
    colors.push(COLORS.archerRed);
    animGroups.push(ARCHER_ANIM_GROUP.LEFT_ARM);

    // 左前臂 - 继续向前
    const leftForearm = new THREE.CylinderGeometry(0.02, 0.016, 0.07, 6);
    leftForearm.rotateX(-Math.PI / 2.5);
    leftForearm.translate(-0.04, 0.38, 0.10);
    parts.push(leftForearm);
    colors.push(COLORS.archerRed);
    animGroups.push(ARCHER_ANIM_GROUP.LEFT_ARM);

    // 左手（握弓的位置 - 更靠近角色中心）
    const leftHandX = -0.02;
    const leftHandY = 0.36;
    const leftHandZ = 0.15;
    const leftHand = new THREE.SphereGeometry(0.018, 6, 5);
    leftHand.translate(leftHandX, leftHandY, leftHandZ);
    parts.push(leftHand);
    colors.push(COLORS.skin);
    animGroups.push(ARCHER_ANIM_GROUP.LEFT_ARM);

    // ==================== 右臂（向后拉，拉弦） ====================
    // 右上臂 - 从肩膀略向后
    const rightUpperArm = new THREE.CylinderGeometry(0.022, 0.02, 0.07, 6);
    rightUpperArm.rotateX(Math.PI / 3);
    rightUpperArm.rotateZ(-0.15);
    rightUpperArm.translate(0.08, 0.40, -0.02);
    parts.push(rightUpperArm);
    colors.push(COLORS.archerRed);
    animGroups.push(ARCHER_ANIM_GROUP.RIGHT_ARM);

    // 右前臂 - 弯曲向胸前（拉弦位置）
    const rightForearm = new THREE.CylinderGeometry(0.02, 0.016, 0.06, 6);
    rightForearm.rotateX(Math.PI / 4);
    rightForearm.rotateZ(-0.3);
    rightForearm.translate(0.06, 0.36, 0.02);
    parts.push(rightForearm);
    colors.push(COLORS.archerRed);
    animGroups.push(ARCHER_ANIM_GROUP.RIGHT_ARM);

    // 右手（拉弦的位置，靠近胸口）
    const rightHandX = 0.04;
    const rightHandY = 0.34;
    const rightHandZ = 0.06;
    const rightHand = new THREE.SphereGeometry(0.018, 6, 5);
    rightHand.translate(rightHandX, rightHandY, rightHandZ);
    parts.push(rightHand);
    colors.push(COLORS.skin);
    animGroups.push(ARCHER_ANIM_GROUP.RIGHT_ARM);

    // ==================== 腿部 ====================
    // 左大腿
    const leftThigh = new THREE.CylinderGeometry(0.028, 0.024, 0.08, 6);
    leftThigh.translate(-0.032, 0.13, 0);
    parts.push(leftThigh);
    colors.push(COLORS.archerRed);
    animGroups.push(ARCHER_ANIM_GROUP.LEFT_LEG);

    // 左小腿
    const leftShin = new THREE.CylinderGeometry(0.024, 0.02, 0.07, 6);
    leftShin.translate(-0.032, 0.05, 0);
    parts.push(leftShin);
    colors.push(COLORS.archerRed);
    animGroups.push(ARCHER_ANIM_GROUP.LEFT_LEG);

    // 左脚
    const leftFoot = new THREE.BoxGeometry(0.032, 0.015, 0.045);
    leftFoot.translate(-0.032, 0.008, 0.008);
    parts.push(leftFoot);
    colors.push(COLORS.woodDark);
    animGroups.push(ARCHER_ANIM_GROUP.LEFT_LEG);

    // 右大腿
    const rightThigh = new THREE.CylinderGeometry(0.028, 0.024, 0.08, 6);
    rightThigh.translate(0.032, 0.13, 0);
    parts.push(rightThigh);
    colors.push(COLORS.archerRed);
    animGroups.push(ARCHER_ANIM_GROUP.RIGHT_LEG);

    // 右小腿
    const rightShin = new THREE.CylinderGeometry(0.024, 0.02, 0.07, 6);
    rightShin.translate(0.032, 0.05, 0);
    parts.push(rightShin);
    colors.push(COLORS.archerRed);
    animGroups.push(ARCHER_ANIM_GROUP.RIGHT_LEG);

    // 右脚
    const rightFoot = new THREE.BoxGeometry(0.032, 0.015, 0.045);
    rightFoot.translate(0.032, 0.008, 0.008);
    parts.push(rightFoot);
    colors.push(COLORS.woodDark);
    animGroups.push(ARCHER_ANIM_GROUP.RIGHT_LEG);

    // ==================== 武器：弓（弓在前方，弧度朝外） ====================
    const bowRadius = 0.12;
    const bowCurve = new THREE.TorusGeometry(bowRadius, 0.012, 6, 16, Math.PI * 0.85);
    bowCurve.rotateZ(Math.PI / 2);
    bowCurve.rotateY(Math.PI / 2);
    bowCurve.translate(leftHandX, leftHandY, leftHandZ);
    parts.push(bowCurve);
    colors.push(COLORS.wood);
    animGroups.push(ARCHER_ANIM_GROUP.BOW);

    // 弓梢（上下两端）
    const bowTipTop = new THREE.CylinderGeometry(0.005, 0.008, 0.02, 5);
    bowTipTop.translate(leftHandX, leftHandY + bowRadius * 0.9, leftHandZ);
    parts.push(bowTipTop);
    colors.push(COLORS.woodDark);
    animGroups.push(ARCHER_ANIM_GROUP.BOW);

    const bowTipBottom = new THREE.CylinderGeometry(0.005, 0.008, 0.02, 5);
    bowTipBottom.translate(leftHandX, leftHandY - bowRadius * 0.9, leftHandZ);
    parts.push(bowTipBottom);
    colors.push(COLORS.woodDark);
    animGroups.push(ARCHER_ANIM_GROUP.BOW);

    // 弓弦（简化为单根直线）
    const bowTopY = leftHandY + bowRadius * 0.85;
    const bowBottomY = leftHandY - bowRadius * 0.85;
    const bowstringLength = bowTopY - bowBottomY;
    const bowstring = new THREE.CylinderGeometry(0.003, 0.003, bowstringLength, 4);
    bowstring.translate(leftHandX, leftHandY, leftHandZ);
    parts.push(bowstring);
    colors.push(new THREE.Color(0xf5f5dc));
    animGroups.push(ARCHER_ANIM_GROUP.BOWSTRING);

    // ==================== 箭（从右手向外射出） ====================
    // 箭杆：从右手位置向前延伸到弓之外
    const arrowLength = 0.22;
    const arrowShaft = new THREE.CylinderGeometry(0.005, 0.005, arrowLength, 5);
    arrowShaft.rotateX(Math.PI / 2);
    arrowShaft.translate(
      0,
      leftHandY,
      rightHandZ + arrowLength / 2
    );
    parts.push(arrowShaft);
    colors.push(COLORS.wood);
    animGroups.push(ARCHER_ANIM_GROUP.ARROW);

    // 箭头（金属，指向前方 Z+）
    const arrowHead = new THREE.ConeGeometry(0.012, 0.035, 4);
    arrowHead.rotateX(Math.PI / 2);
    arrowHead.translate(
      0,
      leftHandY,
      rightHandZ + arrowLength + 0.015
    );
    parts.push(arrowHead);
    colors.push(COLORS.steel);
    animGroups.push(ARCHER_ANIM_GROUP.ARROW);

    // 箭羽（红色，在箭尾，靠近右手）
    for (let i = 0; i < 3; i++) {
      const fletch = new THREE.BoxGeometry(0.018, 0.022, 0.003);
      const fletchAngle = (i / 3) * Math.PI * 2;
      fletch.rotateZ(fletchAngle);
      fletch.translate(
        Math.cos(fletchAngle) * 0.01,
        leftHandY + Math.sin(fletchAngle) * 0.01,
        rightHandZ + 0.02
      );
      parts.push(fletch);
      colors.push(COLORS.tassel);
      animGroups.push(ARCHER_ANIM_GROUP.ARROW);
    }

    return this.mergeGeometriesWithAnimData(parts, colors, animGroups);
  }

  private mergeGeometriesWithAnimData(
    geometries: THREE.BufferGeometry[],
    colors: THREE.Color[],
    animGroups: number[]
  ): THREE.BufferGeometry {
    // Calculate total vertices
    let totalVertices = 0;
    for (const geo of geometries) {
      const nonIndexed = geo.index ? geo.toNonIndexed() : geo;
      totalVertices += nonIndexed.getAttribute('position').count;
    }

    // Create output arrays
    const positions = new Float32Array(totalVertices * 3);
    const normals = new Float32Array(totalVertices * 3);
    const vertexColors = new Float32Array(totalVertices * 3);
    const animGroupAttr = new Float32Array(totalVertices);

    let offset = 0;
    for (let i = 0; i < geometries.length; i++) {
      let geo = geometries[i];
      if (geo.index) {
        geo = geo.toNonIndexed();
      }

      const posAttr = geo.getAttribute('position');
      const normalAttr = geo.getAttribute('normal');
      const color = colors[i];
      const animGroup = animGroups[i];
      const count = posAttr.count;

      // Add subtle color variation
      const variation = 0.94 + Math.random() * 0.12;

      for (let j = 0; j < count; j++) {
        const idx = offset + j;

        // Position
        positions[idx * 3] = posAttr.getX(j);
        positions[idx * 3 + 1] = posAttr.getY(j);
        positions[idx * 3 + 2] = posAttr.getZ(j);

        // Normal
        if (normalAttr) {
          normals[idx * 3] = normalAttr.getX(j);
          normals[idx * 3 + 1] = normalAttr.getY(j);
          normals[idx * 3 + 2] = normalAttr.getZ(j);
        }

        // Color with variation
        vertexColors[idx * 3] = color.r * variation;
        vertexColors[idx * 3 + 1] = color.g * variation;
        vertexColors[idx * 3 + 2] = color.b * variation;

        // Animation group
        animGroupAttr[idx] = animGroup;
      }

      offset += count;
      geometries[i].dispose();
    }

    // Create merged geometry
    const merged = new THREE.BufferGeometry();
    merged.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    merged.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
    merged.setAttribute('color', new THREE.BufferAttribute(vertexColors, 3));
    merged.setAttribute('animGroup', new THREE.BufferAttribute(animGroupAttr, 1));
    merged.computeVertexNormals();

    return merged;
  }

  addArcher(entity: Entity, x: number, y: number, z: number, factionId: FactionId): void {
    // Check if already exists - O(1) lookup
    if (this.entityMap.has(entity)) return;

    const data: ArcherData = {
      entity,
      x,
      y,
      z,
      factionId,
      rotation: 0,
      speed: 0,
      animPhase: Math.random() * Math.PI * 2,
      attackTime: -1,
      attackDuration: 0.65,
    };
    this.archerData.push(data);
    this.entityMap.set(entity, data);
  }

  removeArcher(entity: Entity): void {
    const archer = this.entityMap.get(entity);
    if (!archer) return;

    // Swap-and-pop removal: O(1) instead of O(n) splice
    const index = this.archerData.indexOf(archer);
    if (index !== -1) {
      const last = this.archerData[this.archerData.length - 1];
      this.archerData[index] = last;
      this.archerData.pop();
    }
    this.entityMap.delete(entity);
  }

  updateArcher(entity: Entity, x: number, y: number, z: number, rotation?: number): void {
    const archer = this.entityMap.get(entity);
    if (!archer) return;

    // Calculate speed from movement
    const dx = x - archer.x;
    const dz = z - archer.z;
    const distance = Math.sqrt(dx * dx + dz * dz);

    // Smooth speed transition
    const targetSpeed = Math.min(distance * 30, 1.0);
    archer.speed = archer.speed * 0.85 + targetSpeed * 0.15;

    // Update rotation
    if (rotation !== undefined) {
      archer.rotation = rotation;
    } else if (distance > 0.001) {
      archer.rotation = Math.atan2(dx, dz);
    }

    archer.x = x;
    archer.y = y;
    archer.z = z;
  }

  update(deltaTime: number): void {
    this.time += deltaTime;

    // Update time uniform for all materials
    for (const material of this.materials.values()) {
      material.uniforms.uTime.value = this.time;
    }

    // Group archers by faction for instanced rendering
    const factionGroups = new Map<FactionId, ArcherData[]>();
    for (const archer of this.archerData) {
      if (!factionGroups.has(archer.factionId)) {
        factionGroups.set(archer.factionId, []);
      }
      factionGroups.get(archer.factionId)!.push(archer);
    }

    // Update each faction's instanced mesh
    for (const [factionId, mesh] of this.instancedMeshes) {
      const archerList = factionGroups.get(factionId) || [];
      mesh.count = archerList.length;

      if (archerList.length === 0) continue;

      // Update instance attributes and matrices
      for (let i = 0; i < archerList.length; i++) {
        const archer = archerList[i];

        // Update instance attributes
        if (this.speedAttribute) {
          this.speedAttribute.setX(i, archer.speed);
        }
        if (this.phaseAttribute) {
          this.phaseAttribute.setX(i, archer.animPhase);
        }
        if (this.attackTimeAttribute) {
          this.attackTimeAttribute.setX(i, archer.attackTime);
        }

        // Update transform matrix
        const surfaceOffset = 0.02;
        this.dummy.position.set(archer.x, archer.y + surfaceOffset, archer.z);
        this.dummy.rotation.set(0, archer.rotation, 0);
        this.dummy.scale.setScalar(1.75);
        this.dummy.updateMatrix();
        mesh.setMatrixAt(i, this.dummy.matrix);
      }

      // Mark for GPU update
      mesh.instanceMatrix.needsUpdate = true;
      if (this.speedAttribute) this.speedAttribute.needsUpdate = true;
      if (this.phaseAttribute) this.phaseAttribute.needsUpdate = true;
      if (this.attackTimeAttribute) this.attackTimeAttribute.needsUpdate = true;
    }
  }

  getArcherCount(): number {
    return this.archerData.length;
  }

  clear(): void {
    this.archerData.length = 0;
    for (const mesh of this.instancedMeshes.values()) {
      mesh.count = 0;
    }
  }

  dispose(): void {
    // Remove event listeners
    if (this.attackEventHandler) {
      EventBus.off('unit:attack', this.attackEventHandler);
    }

    // Dispose meshes
    for (const mesh of this.instancedMeshes.values()) {
      mesh.geometry.dispose();
      if (mesh.material instanceof THREE.Material) {
        mesh.material.dispose();
      }
      this.scene.remove(mesh);
    }
    this.instancedMeshes.clear();
    this.materials.clear();
    this.archerData.length = 0;
  }
}
